<template>
    <v-row>
        <v-col cols="12" md="4" class="d-flex align-center">
            <div class="d-flex align-center ml-2">
                <v-checkbox
                    v-model="checked"
                    :disabled="hideAction"
                    @update:model-value="
                        $emit('update', {
                            service: extraService,
                            qty,
                            operation: $event,
                            nettoPriceValue,
                            bruttoPriceValue,
                        })
                    "
                >
                    <template v-slot:label>
                        <div class="text-wrap">
                            {{ extraService.name }}
                            <v-icon v-if="extraService.description" size="small" @click="showDescription = true">
                                mdi-information-outline
                            </v-icon>
                        </div>
                    </template>
                </v-checkbox>
            </div>
            <v-dialog v-model="showDescription" width="500" class="description-modal">
                <v-card class="pa-4" v-html="extraService.description" />
            </v-dialog>
        </v-col>
        <v-col v-if="manualModification" cols="3" md="2">
            <v-text-field
                v-model="nettoPriceValue"
                type="number"
                :rules="[positiveNumberRule('price')]"
                :label="$t('netto')"
                :prefix="supplierCurrency"
            />
        </v-col>
        <v-col v-if="manualModification" cols="3" md="2">
            <v-text-field
                v-model="bruttoPriceValue"
                type="number"
                :rules="[positiveNumberRule('price')]"
                :label="$t('brutto')"
                :prefix="clientCurrency"
            />
        </v-col>
        <v-col v-if="showPrice && !manualModification" cols="3" md="2">
            <span class="text-caption">{{ $t('price') }}</span>
            <div>
                {{ $filters.price(extraServicePriceOne) }}
            </div>
        </v-col>
        <v-col cols="3" md="2">
            <span class="text-caption">{{ $t('price_per') }}</span>
            <div>
                {{ extraService.perPax ? $t('for_guest') : $t('order_service') }}
                {{
                    (extraService.calculationType && extraService.calculationType !== 'BY_SERVICE') ||
                    (extraService.perPax && extraService.calculationType
                        ? $t(`extra_services_details.type.${extraService.calculationType}`)
                        : '')
                }}
            </div>
        </v-col>
        <v-col cols="3" md="2">
            <v-text-field
                v-model="qty"
                :label="$t('quantity')"
                type="number"
                :min="1"
                :max="100"
                :disabled="hideAction"
                @keyup="validateMaxQty"
            />
        </v-col>
        <v-col v-if="showPrice && !manualModification" cols="3" md="2">
            <span class="text-caption">{{ $t('total') }}</span>
            <div>
                {{ $filters.price(extraServicePriceTotal) }}
            </div>
        </v-col>
    </v-row>
    <slot name="additional-options" :extra-service="extraService" :checked="checked" />
</template>

<script lang="ts">
    import {Component, Prop, Watch} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import {salesTermsRsTransformer} from '@/utils/api-helpers'

    //TODO Need refactoring
    @Component
    export class ExtraService extends FormValidationRulesMixin {
        @Prop() extraService
        @Prop({default: false}) hideAction
        @Prop({type: Array, default: () => []}) extraServicesForRequest
        @Prop({default: false, type: Boolean}) manualModification

        qty = 1
        checked = false
        showDescription = false
        nettoPriceValue = null
        bruttoPriceValue = null

        @Watch('qty')
        @Watch('nettoPriceValue')
        @Watch('bruttoPriceValue')
        update() {
            if (this.checked) {
                this.$emit('update', {
                    service: this.extraService,
                    qty: this.qty,
                    operation: true,
                    nettoPriceValue: +this.nettoPriceValue,
                    bruttoPriceValue: +this.bruttoPriceValue,
                })
            }
        }

        mounted() {
            if (this.extraService.count) {
                this.qty = this.extraService.count
            }

            const extraServiceForRequest = !this.manualModification
                ? this.extraServicesForRequest.find(({serviceRPH}) => this.extraService.serviceRPH === serviceRPH)
                : this.extraServicesForRequest.find(({extraServiceId}) => +this.extraService.id === extraServiceId)
            if (extraServiceForRequest) {
                this.qty = extraServiceForRequest.quantity
                this.checked = true
                if (this.manualModification) {
                    this.nettoPriceValue = extraServiceForRequest.nettoPriceValue / extraServiceForRequest.quantity
                    this.bruttoPriceValue = extraServiceForRequest.bruttoPriceValue / extraServiceForRequest.quantity
                }
                return
            }
            if (this.manualModification) {
                this.nettoPriceValue =
                    this.extraServicePrices.supplierPrice.originalAmount || this.extraServicePrices.supplierPrice.amount
                this.bruttoPriceValue =
                    this.extraServicePrices.price.originalAmount || this.extraServicePrices.price.amount
            }
            //TODO Remove this and add initial mandatory extraServices in parent components
            if (this.hideAction) {
                this.checked = true
                this.$emit('update', {service: this.extraService, qty: this.qty, operation: true})
            }
        }

        validateMaxQty(event) {
            if (event.target.value.length > 2) {
                event.preventDefault()
            }
            this.qty = Number(event.target.value) > 100 ? 100 : event.target.value
        }

        get extraServicePriceTotal() {
            return {
                amount: this.extraServicePriceOne.amount * this.qty,
                currency: this.extraServicePriceOne.currency,
            }
        }

        get extraServicePriceOne() {
            const {price} = this.extraServicePrices

            return {
                amount: +(price.amount / (this.extraService.count || 1)).toFixed(2),
                currency: price.currency,
            }
        }

        get priceType() {
            return this.extraService.perPax ? this.$t('tourist') : this.$t('by_service')
        }

        get showPrice() {
            return true
        }

        get extraServicePrices() {
            return salesTermsRsTransformer(this.extraService.salesTerms)
        }

        get clientCurrency() {
            return this.extraServicePrices.price.originalCurrency || this.extraServicePrices.price.currency
        }

        get supplierCurrency() {
            return (
                this.extraServicePrices.supplierPrice.originalCurrency || this.extraServicePrices.supplierPrice.currency
            )
        }
    }

    export default ExtraService
</script>

<style scoped lang="scss">
    :deep(.without-line) {
        .v-input__slot::before {
            border: none;
            display: none;
        }
    }

    :deep(input[type='number']::-webkit-inner-spin-button) {
        opacity: 1;
    }

    .description-modal {
        p {
            margin-bottom: 0;
        }
    }
</style>
